import { useEffect, useState } from "react";
import { Box, Typography, Button, IconButton, Divider } from "@mui/material";
import { Close, Android } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { routes } from "@/config/routes";
import { api } from "@/api";
import { useNavigate } from "react-router-dom";
import { useStoreSettings } from "../contexts/StoreSettingsContext";

interface AddToWalletProps {
  onContinue: () => void;
}

export default function AddToWallet({}: AddToWalletProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [skipCountdown, setSkipCountdown] = useState(5);
  const navigate = useNavigate();
  const { storeSettings, passSettings } = useStoreSettings();

  // Countdown for skip button
  useEffect(() => {
    if (skipCountdown > 0) {
      const timer = setTimeout(() => {
        setSkipCountdown(skipCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [skipCountdown]);

  const handleClose = () => {
    // window.location.href = routes.home;
    navigate(routes.home, { replace: true });
  };

  const handleAddToAppleWallet = async () => {
    console.log("Adding to Apple Wallet");
    try {
      const response = await api.get("/api/wallet/apple-pass", {
        responseType: "blob",
        headers: {
          Accept: "application/vnd.apple.pkpass",
        },
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "membership.pkpass";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      // onContinue();
    } catch (error) {
      console.error("Error downloading Apple Wallet pass:", error);
    }
    // onContinue();
  };

  const handleAddToAndroidWallet = async () => {
    console.log("Adding to Android Wallet");
    const { data } = await api.get<{ saveUrl: string }>(
      "/api/wallet/google/save-link"
    );
    window.open(data.saveUrl, "_blank", "noopener,noreferrer");
    // onContinue();
  };

  const handleSkip = () => {
    // window.location.href = routes.home;
    navigate(routes.home, { replace: true });
  };

  const content = (
    <Box
      sx={{
        minHeight: isDesktop ? "auto" : "100vh",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {/* Top Section - Green Background */}
      <Box
        sx={{
          height: isDesktop ? "200px" : "45vh",
          backgroundColor: "#A9C191",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: 3,
          textAlign: "center",
          color: "white",
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          <Close />
        </IconButton>

        {/* Title */}
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            color: "white",
            fontSize: isDesktop ? "20px" : "24px",
            zIndex: 2,
          }}
        >
          One more step left!
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: "white",
            opacity: 0.9,
            lineHeight: 1.6,
            fontSize: isDesktop ? "14px" : "16px",
            mb: 4,
            zIndex: 2,
          }}
        >
          Add your membership card to your wallet app so you'll always have it
          with you.
        </Typography>

        {/* Membership Card Container - Positioned to bridge both sections */}
        <Box
          sx={{
            position: "absolute",
            bottom: isDesktop ? "-40px" : "-80px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          {/* Membership Card */}
          <Box
            sx={{
              position: "relative",
              width: isDesktop ? "200px" : "280px",
              height: isDesktop ? "120px" : "160px",
              backgroundColor: "#A9C191",
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: 2,
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
            }}
          >
            {/* Store Logo */}
            <img
              src={storeSettings?.logoUrl || '/icons/logo.svg'}
              alt={storeSettings?.text || "Logo"}
              style={{
                width: "80%",
                height: "80%",
                objectFit: "contain",
                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
              }}
            />
          </Box>
        </Box>

        {/* Card Top Icon - Full width overlay */}
        <Box
          sx={{
            position: "absolute",
            bottom: isDesktop ? "-100px" : "-120px",
            left: "0",
            right: "0",
            width: isDesktop ? "100%" : "100vw",
            zIndex: 4, // Higher than bottom section (z-index: 5)
            display: "flex",
            justifyContent: "center",
          }}
        >
          <img
            src="/icons/icon_card_top.svg"
            alt="Card Top"
            style={{
              marginTop: 14,
              width: "100%",
              height: "auto",
              maxWidth: "100vw",
            }}
          />
        </Box>
      </Box>

      {/* Bottom Section - White Background */}
      <Box
        sx={{
          height: isDesktop ? "auto" : "60%",
          backgroundColor: "#F5F4F8",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          p: 3,
          pb: 6,
          mt: isDesktop ? 2 : 4,
          zIndex: 5, // Higher than card top icon (z-index: 4)
        }}
      >
        {/* Action Buttons */}
        <Box
          sx={{
            mb: 4,
            mt: isDesktop ? "40px" : "10px",
            textAlignLast: "center",
          }}
        >
          {/* Add to Apple Wallet Button */}
          <img
            src="/icons/ic_apple_wallet.svg"
            alt="Apple Wallet"
            onClick={handleAddToAppleWallet}
            style={{
              height: isDesktop ? "36px" : "44px",
            }}
          />

          <Divider sx={{ my: 2 }} />

          {/* Android Option */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mb: 4,
            }}
          >
            <Android
              sx={{ color: "#666", fontSize: isDesktop ? "24px" : "32px" }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  fontSize: isDesktop ? "12px" : "14px",
                }}
              >
                Using an Android Device?
              </Typography>
              <Typography
                variant="body2"
                onClick={handleAddToAndroidWallet}
                sx={{
                  color: "#A9C191",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: isDesktop ? "12px" : "14px",
                }}
              >
                Please switch here
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Skip Button */}
        <Button
          variant="contained"
          size="large"
          onClick={handleSkip}
          disabled={skipCountdown > 0}
          sx={{
            width: "100%",
            height: "48px",
            borderRadius: "24px",
            borderColor: "#E0E0E0",
            color: "white",
            backgroundColor: "#A9C191",
            textTransform: "none",
            fontSize: isDesktop ? "14px" : "16px",
            fontWeight: 600,
            "&:hover": {
              borderColor: "#A9C191",
              color: "white",
            },
            "&:disabled": {
              borderColor: "#E0E0E0",
              color: "#999",
            },
          }}
        >
          Skip {skipCountdown > 0 ? `(${skipCountdown}s)` : ""}
        </Button>
      </Box>
    </Box>
  );

  // Desktop layout - Dialog
  if (isDesktop) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          position: "relative",
          background:
            `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${passSettings?.backgroundUrl || '/images/background.png'}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "24px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            overflow: "hidden",
            maxWidth: "400px",
            width: "100%",
            maxHeight: "80vh",
          }}
        >
          {content}
        </Box>
      </Box>
    );
  }

  // Mobile layout - Full screen
  return content;
}
